import * as React from 'react';
import { creditFeedbackService } from '@/services/creditFeedbackService';
import type { CreditFeedbackCampaign, SubmitCreditFeedbackResponse } from '@/types/creditFeedback';

const DISMISS_KEY_PREFIX = 'credit-feedback-dismissed:';

function getDismissKey(campaignId: string) {
  return `${DISMISS_KEY_PREFIX}${campaignId}`;
}

function isDismissedThisSession(campaignId: string): boolean {
  try {
    return sessionStorage.getItem(getDismissKey(campaignId)) === '1';
  } catch {
    return false;
  }
}

function dismissForSession(campaignId: string) {
  try {
    sessionStorage.setItem(getDismissKey(campaignId), '1');
  } catch {
    // ignore
  }
}

interface UseCreditFeedbackPopoverOptions {
  credits: number;
  lifebarMax: number;
  enabled?: boolean;
}

interface UseCreditFeedbackPopoverResult {
  isOpen: boolean;
  campaign: CreditFeedbackCampaign | null;
  isSubmitting: boolean;
  dismiss: () => void;
  submit: (answers: Record<string, string>) => Promise<SubmitCreditFeedbackResponse | null>;
}

/** Polls feedback eligibility when credits change and manages popover state. */
export function useCreditFeedbackPopover({
  credits,
  lifebarMax,
  enabled = true,
}: UseCreditFeedbackPopoverOptions): UseCreditFeedbackPopoverResult {
  const [campaign, setCampaign] = React.useState<CreditFeedbackCampaign | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) {
      setIsOpen(false);
      setCampaign(null);
      return;
    }

    const controller = new AbortController();

    creditFeedbackService
      .getStatus(controller.signal)
      .then((status) => {
        if (!status.should_prompt || !status.campaign) {
          setIsOpen(false);
          setCampaign(null);
          return;
        }
        if (isDismissedThisSession(status.campaign.id)) {
          setIsOpen(false);
          setCampaign(status.campaign);
          return;
        }
        setCampaign(status.campaign);
        setIsOpen(true);
      })
      .catch(() => {
        setIsOpen(false);
      });

    return () => controller.abort();
  }, [credits, lifebarMax, enabled]);

  const dismiss = React.useCallback(() => {
    if (campaign) {
      dismissForSession(campaign.id);
    }
    setIsOpen(false);
  }, [campaign]);

  const submit = React.useCallback(
    async (answers: Record<string, string>) => {
      if (!campaign) return null;
      setIsSubmitting(true);
      try {
        const result = await creditFeedbackService.submit({
          campaign_id: campaign.id,
          answers,
        });
        setIsOpen(false);
        return result;
      } finally {
        setIsSubmitting(false);
      }
    },
    [campaign]
  );

  return {
    isOpen,
    campaign,
    isSubmitting,
    dismiss,
    submit,
  };
}
